import { Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { formatCurrency, formatNumber } from '@angular/common';
import { FormControl } from '@angular/forms';
import {
  update_description,
  update_hard_debt,
  update_units,
} from './graphql/alterDealInfo';
import {
  add_supportive_services_included_type,
  update_project_attributes,
} from './graphql/editAttributes';
import { Apollo } from 'apollo-angular';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddSupportiveServicesIncludedConfirmationComponent } from './add-supportive-services-included-confirmation/add-supportive-services-included-confirmation.component';

const UPDATE_DESCRIPTION = update_description;
const UPDATE_PROJECT_ATTRIBUTES = update_project_attributes;

export interface AttributeObject {
  id: string;
  name: string;
}

export interface AttributesTable {
  name: string;
  value: Array<AttributeObject>;
}

export interface KPITable {
  name: string;
  value: string;
}

export interface CensusTable {
  censusName: string;
  val: string;
}

export interface UnitTable {
  unitName: string;
  number: number;
  newNumber: FormControl;
}

@Component({
    selector: 'deal-information',
    templateUrl: './deal-information.component.html',
    styleUrls: ['./deal-information.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DealInformationComponent implements OnInit {
  @Input() project: any;
  @Input() attributeTypes: any;
  @Input() permissions: any;
  @Input() ltmodels: any;
  @ViewChild('KPISort') public KPISort: MatSort;
  @ViewChild('censusSort') public censusSort: MatSort;
  @ViewChild('unitSort') public unitSort: MatSort;

  description: string;

  projectAttributes: any[];
  attributeColumns: string[] = ['name', 'value'];
  thinAttributeColumns: string[] = ['value'];
  attributeDataSource: MatTableDataSource<AttributesTable>;
  attributeEditMode: boolean = false;
  attributeEditData: any[] = [];
  attributeEditDeleteList: any[] = [];
  attributeEditChangeList: any[] = [];
  attributeEditCreateList: any[] = [];

  KPIColumns: string[] = ['name', 'value'];
  KPIDataSource: MatTableDataSource<KPITable>;
  formattedKPIs: any;

  censusColumns: string[] = ['censusName', 'val'];
  censusDataSource: MatTableDataSource<CensusTable>;

  unitsEditMode: boolean = false;
  unitColumns: string[] = ['unitName', 'number'];
  unitDataSource: MatTableDataSource<UnitTable>;
  formattedUnits: any;

  descriptionEditMode: boolean = false;
  desc: FormControl;
  descChanged: boolean = false;

  hasModels: boolean = false;
  newHardDebt: FormControl = new FormControl();
  hardDebtEditMode = false;

  constructor(
    private apollo: Apollo,
    private _snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}
  ngOnInit() {
    this.projectAttributes = this.project.projectAttributes;
    this.attributeDataSource = new MatTableDataSource(
      this.formatAttributes(this.projectAttributes)
    );
    this.formattedKPIs = this.formatKPIs(this.project);
    this.KPIDataSource = new MatTableDataSource(this.formattedKPIs);
    this.formattedUnits = this.formatUnits(this.project.properties);
    this.unitDataSource = new MatTableDataSource(this.formattedUnits);
    this.censusDataSource = new MatTableDataSource(
      this.formatCensus(this.project.properties)
    );
    this.description = this.project.description;
    this.desc = new FormControl(this.description);

    this.isMobile = this.getIsMobile();
    window.onresize = () => {
      this.isMobile = this.getIsMobile();
    };
  }

  isMobile = false;
  getIsMobile(): boolean {
    const w = document.documentElement.clientWidth;
    const breakpoint = 1475;
    return w < breakpoint;
  }

  ngAfterViewInit() {
    this.KPIDataSource.sort = this.censusSort;
    this.censusDataSource.sort = this.censusSort;
    this.unitDataSource.sort = this.unitSort;
  }
  canEditAnAttribute(): boolean {
    return this.attributeTypes.find(
      (attributeType: any) => attributeType.permissions?._edit === true
    );
  }
  toggleDescriptionEditMode() {
    if (this.descriptionEditMode && this.descChanged) {
      this.apollo
        .mutate({
          mutation: UPDATE_DESCRIPTION,
          variables: {
            id: this.project.id,
            description: this.desc.value,
          },
        })
        .subscribe(({ data }) => {
          // @ts-ignore
          this.description = data.updateDescription.description;
          this.desc.setValue(this.description);
          this.descChanged = false;
          this.openSnackBar();
        });
    }
    this.descriptionEditMode = !this.descriptionEditMode;
  }

  toggleUnitsEditMode() {
    if (this.unitsEditMode) {
      let isUpdate = false;

      for (let i = 0; i < this.formattedUnits.length; i++) {
        if (
          this.formattedUnits[i].number !==
          this.formattedUnits[i].newNumber.value
        ) {
          isUpdate = true;
          break;
        }
      }

      if (isUpdate) {
        let buildings = null;
        let total_units = null;
        let total_lihtc_units = null;
        let total_market_rate_units = null;
        let total_rental_assistance_units = null;
        let total_common_units = null;
        let total_family_units = null;
        let total_senior_units = null;

        for (let i = 0; i < this.formattedUnits.length; i++) {
          if (this.formattedUnits[i].unitName === 'Buildings') {
            buildings = this.formattedUnits[i].newNumber.value
              ? parseInt(this.formattedUnits[i].newNumber.value)
              : null;
          } else if (this.formattedUnits[i].unitName === 'Total Units') {
            total_units = this.formattedUnits[i].newNumber.value
              ? parseInt(this.formattedUnits[i].newNumber.value)
              : null;
          } else if (this.formattedUnits[i].unitName === 'Total LIHTC Units') {
            total_lihtc_units = this.formattedUnits[i].newNumber.value
              ? parseInt(this.formattedUnits[i].newNumber.value)
              : null;
          } else if (
            this.formattedUnits[i].unitName === 'Total Market Rate Units'
          ) {
            total_market_rate_units = this.formattedUnits[i].newNumber.value
              ? parseInt(this.formattedUnits[i].newNumber.value)
              : null;
          } else if (
            this.formattedUnits[i].unitName === 'Total Rental Assistance Units'
          ) {
            total_rental_assistance_units = this.formattedUnits[i].newNumber
              .value
              ? parseInt(this.formattedUnits[i].newNumber.value)
              : null;
          } else if (this.formattedUnits[i].unitName === 'Total Common Units') {
            total_common_units = this.formattedUnits[i].newNumber.value
              ? parseInt(this.formattedUnits[i].newNumber.value)
              : null;
          } else if (this.formattedUnits[i].unitName === 'Total Family Units') {
            total_family_units = this.formattedUnits[i].newNumber.value
              ? parseInt(this.formattedUnits[i].newNumber.value)
              : null;
          } else if (this.formattedUnits[i].unitName === 'Total Senior Units') {
            total_senior_units = this.formattedUnits[i].newNumber.value
              ? parseInt(this.formattedUnits[i].newNumber.value)
              : null;
          }
        }

        this.apollo
          .mutate({
            mutation: update_units,
            variables: {
              id: this.project.properties[0].id,
              buildings: buildings,
              total_units: total_units,
              total_lihtc_units: total_lihtc_units,
              total_market_rate_units: total_market_rate_units,
              total_rental_assistance_units: total_rental_assistance_units,
              total_common_units: total_common_units,
              total_family_units: total_family_units,
              total_senior_units: total_senior_units,
            },
          })
          .subscribe(({ data }) => {
            for (let i = 0; i < this.formattedUnits.length; i++) {
              this.formattedUnits[i].number = this.formattedUnits[i].newNumber
                .value
                ? this.formattedUnits[i].newNumber.value
                : 0;
            }
            this.unitDataSource = new MatTableDataSource(this.formattedUnits);
            this.openSnackBar();
          });
      }
    }
    this.unitsEditMode = !this.unitsEditMode;
  }

  toggleAttributeEditMode() {
    if (this.attributeEditMode) {
      this.saveAttributes();
    }
    this.attributeEditMode = !this.attributeEditMode;
    this.attributeEditData = [];
    this.attributeEditChangeList = [];
    this.attributeEditCreateList = [];
    this.attributeEditDeleteList = [];
    if (this.attributeEditMode) {
      for (let i = 0; i < this.attributeTypes.length; i += 1) {
        let add: any = {
          attType: null,
          data: [],
        };
        add.attType = this.attributeTypes[i];
        for (let j = 0; j < this.projectAttributes.length; j += 1) {
          if (
            add.attType.name ===
            this.projectAttributes[j].attribute.attributeType.name
          ) {
            add.data.push({
              attribute: {
                attributeType: add.attType,
                name: this.projectAttributes[j].attribute.name,
                id: '' + this.projectAttributes[j].id,
              },
            });
          }
        }
        if (add.data.length === 0) {
          add.data.push({
            attribute: {
              attributeType: add.attType,
              name: '',
              id: null,
            },
          });
        }
        this.attributeEditData.push(add);
      }
      for (let i = 0; i < this.attributeEditData.length; i++) {
        let dataClone = structuredClone(this.attributeEditData[i]);
        dataClone.attType.attributes.sort((a: any, b: any) =>
          a.name.localeCompare(b.name)
        );
        this.attributeEditData[i] = dataClone;
      }
    }
  }

  changeAttribute($event: any, id: any) {
    if (id && !this.attributeEditChangeList.includes(id)) {
      this.attributeEditChangeList.push(id);
    }
  }

  addBlankAttribute(attType: any) {
    for (let i = 0; i < this.attributeEditData.length; i += 1) {
      if (attType.name === this.attributeEditData[i].attType.name) {
        this.attributeEditData[i].data.push({
          attribute: {
            attributeType: this.attributeEditData[i].attType,
            name: '',
            id: null,
          },
        });
      }
    }
  }

  removeAttribute(att: any) {
    for (let i = 0; i < this.attributeEditData.length; i += 1) {
      if (att.attributeType.name === this.attributeEditData[i].attType.name) {
        for (let j = 0; j < this.attributeEditData[i].data.length; j += 1) {
          if (att.name === this.attributeEditData[i].data[j].attribute.name) {
            if (this.attributeEditData[i].data[j].attribute.id) {
              this.attributeEditDeleteList.push(
                this.attributeEditData[i].data[j].attribute.id
              );
              if (
                this.attributeEditChangeList.indexOf(
                  this.attributeEditData[i].data[j].attribute.id
                ) >= 0
              ) {
                this.attributeEditChangeList.splice(
                  this.attributeEditChangeList.indexOf(
                    this.attributeEditData[i].data[j].attribute.id
                  ),
                  1
                );
              }
            }
            this.attributeEditData[i].data.splice(j, 1);
            j -= 1;
          }
        }
        if (this.attributeEditData[i].data.length === 0) {
          this.addBlankAttribute(this.attributeEditData[i].attType);
        }
      }
    }
  }

  saveAttributes() {
    //get the full list of attributes with ids
    let withIds: any[] = [];
    for (let i = 0; i < this.attributeEditData.length; i += 1) {
      for (let j = 0; j < this.attributeEditData[i].data.length; j += 1) {
        if (this.attributeEditData[i].data[j].attribute.id) {
          for (
            let l = 0;
            l < this.attributeEditData[i].attType.attributes.length;
            l += 1
          ) {
            if (
              this.attributeEditData[i].data[j].attribute.name ===
              this.attributeEditData[i].attType.attributes[l].name
            ) {
              if (
                withIds.includes(
                  this.attributeEditData[i].attType.attributes[l].id
                )
              ) {
                if (
                  !this.attributeEditDeleteList.includes(
                    this.attributeEditData[i].data[j].attribute.id
                  )
                ) {
                  this.attributeEditDeleteList.push(
                    this.attributeEditData[i].data[j].attribute.id
                  );
                }
                if (
                  this.attributeEditChangeList.includes(
                    this.attributeEditData[i].data[j].attribute.id
                  )
                ) {
                  this.attributeEditChangeList.splice(
                    this.attributeEditChangeList.indexOf(
                      this.attributeEditData[i].data[j].attribute.id
                    ),
                    1
                  );
                }
              } else {
                withIds.push(
                  this.attributeEditData[i].attType.attributes[l].id
                );
              }
            }
          }
        }
      }
    }

    //get the full list of attributes to create
    for (let i = 0; i < this.attributeEditData.length; i += 1) {
      for (let j = 0; j < this.attributeEditData[i].data.length; j += 1) {
        if (
          !this.attributeEditData[i].data[j].attribute.id &&
          this.attributeEditData[i].data[j].attribute.name !== ''
        ) {
          let createAtt = {
            attribute_id: null,
            active: true,
          };
          for (
            let k = 0;
            k < this.attributeEditData[i].attType.attributes.length;
            k += 1
          ) {
            if (
              this.attributeEditData[i].data[j].attribute.name ===
              this.attributeEditData[i].attType.attributes[k].name
            ) {
              createAtt.attribute_id =
                this.attributeEditData[i].attType.attributes[k].id;
            }
          }
          if (!withIds.includes(createAtt.attribute_id)) {
            this.attributeEditCreateList.push(createAtt);
          }
        }
      }
    }

    //make sure there are no duplicates in the update list
    let foundAtts: any = [];
    for (let i = 0; i < this.attributeEditChangeList.length; i += 1) {
      for (let j = 0; j < this.attributeEditData.length; j += 1) {
        for (let k = 0; k < this.attributeEditData[j].data.length; k += 1) {
          if (
            this.attributeEditData[j].data[k].attribute.id ===
            this.attributeEditChangeList[i]
          ) {
            for (
              let l = 0;
              l < this.attributeEditData[j].attType.attributes.length;
              l += 1
            ) {
              if (
                this.attributeEditData[j].data[k].attribute.name ===
                this.attributeEditData[j].attType.attributes[l].name
              ) {
                for (
                  let n = 0;
                  n < this.attributeEditData[j].data.length;
                  n += 1
                ) {
                  if (
                    n != k &&
                    this.attributeEditData[j].data[n].attribute.name ==
                      this.attributeEditData[j].data[k].attribute.name
                  ) {
                    if (
                      this.attributeEditData[j].data[n].attribute.id !== null
                    ) {
                      this.attributeEditDeleteList.push(
                        this.attributeEditData[j].data[n].attribute.id
                      );
                    }
                    this.attributeEditData[j].data.splice(n, 1);
                    n -= 1;
                  }
                }
                if (
                  !foundAtts.includes(
                    this.attributeEditData[j].attType.attributes[l].id
                  )
                ) {
                  foundAtts.push(
                    this.attributeEditData[j].attType.attributes[l].id
                  );
                } else {
                  if (
                    !this.attributeEditDeleteList.includes(
                      this.attributeEditData[j].data[k].attribute.id
                    )
                  ) {
                    this.attributeEditDeleteList.push(
                      this.attributeEditData[j].data[k].attribute.id
                    );
                  }
                }
              }
            }
          }
        }
      }
    }

    for (let i = 0; i < this.attributeEditCreateList.length; i += 1) {
      if (!foundAtts.includes(this.attributeEditCreateList[i].attribute_id)) {
        foundAtts.push(this.attributeEditCreateList[i].attribute_id);
      } else {
        this.attributeEditCreateList.splice(i, 1);
        i -= 1;
      }
    }

    //Build update object- id, attribute_id, active=1
    let toUpdate: any[] = [];
    for (let i = 0; i < this.attributeEditChangeList.length; i += 1) {
      let id = this.attributeEditChangeList[i];
      for (let j = 0; j < this.attributeEditData.length; j += 1) {
        for (let k = 0; k < this.attributeEditData[j].data.length; k += 1) {
          if (this.attributeEditData[j].data[k].attribute.id === id) {
            for (
              let l = 0;
              l < this.attributeEditData[j].attType.attributes.length;
              l += 1
            ) {
              if (
                this.attributeEditData[j].data[k].attribute.name ===
                this.attributeEditData[j].attType.attributes[l].name
              ) {
                let updateAtt = {
                  active: true,
                  id: id,
                  attribute_id:
                    this.attributeEditData[j].attType.attributes[l].id,
                };
                toUpdate.push(updateAtt);
              }
            }
          }
        }
      }
    }
    this.attributeEditChangeList = toUpdate;
    for (let i = 0; i < this.attributeEditCreateList.length; i++) {
      this.attributeEditChangeList.push(this.attributeEditCreateList[i]);
    }

    this.apollo
      .mutate({
        mutation: UPDATE_PROJECT_ATTRIBUTES,
        variables: {
          input: {
            id: this.project.id,
            projectAttributes: {
              upsert: this.attributeEditChangeList,
              delete: this.attributeEditDeleteList,
            },
          },
        },
      })
      .subscribe(({ data }) => {
        // @ts-ignore
        let results = data.updateProjectAttributes.projectAttributes;
        this.projectAttributes = results;
        this.attributeDataSource = new MatTableDataSource(
          this.formatAttributes(this.projectAttributes)
        );
        this.openSnackBar();
      });
  }

  openSnackBar() {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = 5000;
    this._snackBar.open('Data saved successfully.', 'Ok', config);
  }

  formatAttributes(att: any): Array<AttributesTable> {
    let format: Array<AttributesTable> = [];
    for (let i = 0; i < this.attributeTypes.length; i++) {
      let foundval = false;
      if (att && att.length > 0) {
        let attribute: Array<AttributeObject> = [];
        for (let j = 0; j < att.length; j++) {
          if (this.attributeTypes[i].id === att[j].attribute.attributeType.id) {
            foundval = true;
            attribute.push({
              name: att[j].attribute.name,
              id: att[j].attribute.id,
            });
          }
        }
        if (attribute.length > 0) {
          format.push({
            name: this.attributeTypes[i].name,
            value: attribute,
          });
        }
      }
      if (!foundval) {
        format.push({
          name: this.attributeTypes[i].name,
          value: [],
        });
      }
    }
    return format;
  }

  formatKPIs(kpi: any): Array<KPITable> {
    let format: Array<KPITable> = [];
    let totalCredits: number = 0;
    let totalEquity: number = 0;

    //Get model for this deal
    let latestModel = [];
    if (kpi.ltmodels && kpi.ltmodels.length > 0) {
      let active = [];
      let closed = [];

      for (let i = 0; i < kpi.ltmodels.length; i++) {
        if (kpi.ltmodels[i]['active_model'] === true) {
          active = kpi.ltmodels[i];
        }

        if (
          kpi.ltmodels[i]['milestone'] &&
          kpi.ltmodels[i]['milestone']['name'] === 'Closed'
        ) {
          closed = kpi.ltmodels[i];
        }
      }

      if (active) {
        latestModel = active;
      } else if (closed) {
        latestModel = closed;
      }
    }

    //Count Total Credits and Equity Amount
    if (kpi.projectCredits && kpi.projectCredits.length > 0) {
      for (let i = 0; i < kpi.projectCredits.length; i++) {
        //Get Credits from Project Credits
        if (
          kpi.projectCredits[i].crea_syndicated &&
          kpi.projectCredits[i].amount
        ) {
          totalCredits += kpi.projectCredits[i].amount;
        }

        //If there is a model with info in Price Per Credits
        if (
          latestModel &&
          latestModel.kpiset &&
          latestModel.kpiset.pricePerCredits
        ) {
          for (let j = 0; j < latestModel.kpiset.pricePerCredits.length; j++) {
            let projectCreditType =
              kpi.projectCredits[i].taxCreditType?.level +
              ' ' +
              kpi.projectCredits[i].taxCreditType?.name;

            if (
              projectCreditType ===
                latestModel.kpiset.pricePerCredits[j].benefitType?.name &&
              kpi.projectCredits[i].crea_syndicated
            ) {
              totalEquity +=
                latestModel.kpiset.pricePerCredits[j].equity_amount;
            }
          }
        }

        //If there's no LT Model, get the equity amount from Project Credits and Hard Debt from Project Details
        else if (
          kpi.projectCredits[i].crea_syndicated &&
          kpi.projectCredits[i].equity_amount
        ) {
          totalEquity += kpi.projectCredits[i].equity_amount;
        }
      }
    }

    if (!Array.isArray(latestModel)) {
      this.hasModels = true;

      format.push({
        name: 'Hard Debt',
        value:
          latestModel['kpiset'] &&
          latestModel['kpiset']['kpiset_detail']['hard_debt']
            ? formatNumber(
                latestModel['kpiset']['kpiset_detail']['hard_debt'] * 100,
                'EN',
                '1.0-2'
              ).toString() + '%'
            : '-',
      });

      format.push({
        name: 'Lower Tier Yield',
        value:
          latestModel['kpiset'] &&
          latestModel['kpiset']['kpiset_detail']['lower_tier_yield']
            ? (
                latestModel['kpiset']['kpiset_detail']['lower_tier_yield'] * 100
              ).toString() + '%'
            : '-',
      });
    } else {
      if (this.project['projectDetail']['leverage']) {
        this.newHardDebt = new FormControl(
          parseFloat(this.project['projectDetail']['leverage']) * 100
        );
      }

      format.push({
        name: 'Hard Debt',
        value: this.project['projectDetail']['leverage']
          ? formatNumber(
              this.project['projectDetail']['leverage'] * 100,
              'EN',
              '1.0-2'
            ).toString() + '%'
          : '-',
      });

      format.push({
        name: 'Lower Tier Yield',
        value: '-',
      });
    }

    format.push({
      name: 'Total Credits',
      value: formatCurrency(totalCredits, 'EN', '$', 'USD', '1.0-0'),
    });

    format.push({
      name: 'Total LT Equity',
      value: formatCurrency(totalEquity, 'EN', '$', 'USD', '1.0-0'),
    });
    return format;
  }

  unitsAreValid(): boolean {
    for (let i = 0; i < this.unitDataSource.filteredData.length; i++) {
      if (this.unitDataSource.filteredData[i].newNumber.errors) {
        return false;
      }
    }
    return true;
  }

  getAttributeOptions(name: any) {
    for (let i = 0; i < this.attributeTypes.length; i += 1) {
      if (this.attributeTypes[i].name === name) {
        return this.attributeTypes[i].attributes;
      }
    }
    return [];
  }

  formatCensus(census: any): Array<CensusTable> {
    let format: Array<CensusTable> = [];
    for (let i = 0; i < census.length; i++) {
      if (
        census[i]['entity'] &&
        census[i]['entity']['entityAddresses'] &&
        census[i]['entity']['entityAddresses'].length > 0
      ) {
        for (
          let j = 0;
          j < census[i]['entity']['entityAddresses'].length;
          j++
        ) {
          if (census[i]['entity']['entityAddresses'][j]['address']) {
            format.push({
              censusName: 'Qualified Census Tract',
              val:
                census[i]['entity']['entityAddresses'][j]['address'][
                  'is_qct'
                ] !== null
                  ? this.yesNoFormat(
                      census[i]['entity']['entityAddresses'][j]['address'][
                        'is_qct'
                      ]
                    )
                  : '-',
            });
            format.push({
              censusName: 'Difficult to Develop Area',
              val:
                census[i]['entity']['entityAddresses'][j]['address'][
                  'is_dda'
                ] !== null
                  ? this.yesNoFormat(
                      census[i]['entity']['entityAddresses'][j]['address'][
                        'is_dda'
                      ]
                    )
                  : '-',
            });
            format.push({
              censusName: 'Minority Population Percentage',
              val:
                census[i]['entity']['entityAddresses'][j]['address'][
                  'minority_pop_percent'
                ] !== null
                  ? census[i]['entity']['entityAddresses'][j]['address'][
                      'minority_pop_percent'
                    ] *
                      100 +
                    '%'
                  : '-',
            });
            format.push({
              censusName: 'ACS Year',
              val:
                census[i]['entity']['entityAddresses'][j]['address'][
                  'acs_year'
                ] !== null
                  ? census[i]['entity']['entityAddresses'][j]['address'][
                      'acs_year'
                    ]
                  : '-',
            });
          }
        }
      }
    }
    return format;
  }

  formatUnits(units: any): Array<UnitTable> {
    let format: Array<UnitTable> = [];
    for (let i = 0; i < units.length; i++) {
      format.push({
        unitName: 'Total Units',
        number: units[i]['total_units'] ? units[i]['total_units'] : 0,
        newNumber: new FormControl(units[i]['total_units']),
      });

      format.push({
        unitName: 'Total LIHTC Units',
        number: units[i]['total_lihtc_units']
          ? units[i]['total_lihtc_units']
          : 0,
        newNumber: new FormControl(units[i]['total_lihtc_units']),
      });

      format.push({
        unitName: 'Total Market Rate Units',
        number: units[i]['total_market_rate_units']
          ? units[i]['total_market_rate_units']
          : 0,
        newNumber: new FormControl(units[i]['total_market_rate_units']),
      });

      format.push({
        unitName: 'Total Rental Assistance Units',
        number: units[i]['total_rental_assistance_units']
          ? units[i]['total_rental_assistance_units']
          : 0,
        newNumber: new FormControl(units[i]['total_rental_assistance_units']),
      });

      format.push({
        unitName: 'Total Common Units',
        number: units[i]['total_common_units']
          ? units[i]['total_common_units']
          : 0,
        newNumber: new FormControl(units[i]['total_common_units']),
      });

      format.push({
        unitName: 'Buildings',
        number: units[i]['buildings'] ? units[i]['buildings'] : 0,
        newNumber: new FormControl(units[i]['buildings']),
      });

      format.push({
        unitName: 'Total Family Units',
        number: units[i]['total_family_units']
          ? units[i]['total_family_units']
          : 0,
        newNumber: new FormControl(units[i]['total_family_units']),
      });

      format.push({
        unitName: 'Total Senior Units',
        number: units[i]['total_senior_units']
          ? units[i]['total_senior_units']
          : 0,
        newNumber: new FormControl(units[i]['total_senior_units']),
      });
    }
    return format;
  }

  yesNoFormat(value: boolean): string {
    return value ? 'Yes' : !value ? 'No' : 'N/A';
  }

  toggleHardDebt() {
    if (this.hardDebtEditMode) {
      let oldHardDebt;
      let hardDebtKey = 0;
      let isUpdate = false;
      for (let i = 0; i < this.formattedKPIs.length; i++) {
        if (this.formattedKPIs[i].name === 'Hard Debt') {
          oldHardDebt = this.formattedKPIs[i].value;
          hardDebtKey = i;
          break;
        }
      }

      if (oldHardDebt === '-' && this.newHardDebt !== null) {
        isUpdate = true;
      } else if (
        oldHardDebt !== '-' &&
        parseFloat(oldHardDebt) !== this.newHardDebt.value
      ) {
        isUpdate = true;
      }

      if (isUpdate) {
        this.apollo
          .mutate({
            mutation: update_hard_debt,
            variables: {
              id: this.project.projectDetail.id,
              leverage: this.newHardDebt.value
                ? this.newHardDebt.value / 100
                : null,
            },
          })
          .subscribe((response: any) => {
            let { data } = response;
            this.formattedKPIs[hardDebtKey].value = data.updateHardDebt.leverage
              ? formatNumber(
                  data.updateHardDebt.leverage * 100,
                  'EN',
                  '1.0-2'
                ).toString() + '%'
              : '-';
            this.newHardDebt = data.updateHardDebt.leverage
              ? new FormControl(parseFloat(data.updateHardDebt.leverage) * 100)
              : new FormControl();
            this.KPIDataSource = new MatTableDataSource(this.formattedKPIs);
            this.openSnackBar();
          });
      }
    }
    this.hardDebtEditMode = !this.hardDebtEditMode;
  }

  addSupportiveServicesIncluded(types: any, id: string) {
    const ref: MatDialogRef<AddSupportiveServicesIncludedConfirmationComponent> =
      this.dialog.open(AddSupportiveServicesIncludedConfirmationComponent, {
        data: {
          attributes: types,
        },
        backdropClass: 'confirmDialogComponent',
        hasBackdrop: true,
      });
    ref.afterClosed().subscribe((modalData) => {
      if (modalData.clicked === 'Ok') {
        this.apollo
          .mutate({
            mutation: add_supportive_services_included_type,
            variables: {
              name: modalData.name,
              attribute_type_id: id,
            },
          })
          .subscribe(({ data }) => {
            let attributes = [...this.attributeTypes];
            for (let i = 0; i < attributes.length; i++) {
              if (attributes[i].id === id) {
                let arr = { ...attributes[i] };
                // @ts-ignore
                let clone: any = structuredClone(data);
                arr.attributes = [
                  ...attributes[i].attributes,
                  clone.addSupportiveServicesIncluded,
                ];
                attributes[i] = arr;
                break;
              }
            }
            this.attributeTypes = attributes;
            this.toggleAttributeEditMode();
            this.attributeDataSource = new MatTableDataSource(
              this.formatAttributes(this.projectAttributes)
            );
          });
      }
    });
  }
}
